import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Users, UserPlus, Calendar, Search, Home, Menu, X, LogOut } from 'lucide-react';
import './App.css';

// Import components
import Dashboard from './components/Dashboard';
import PatientRegistration from './components/PatientRegistration';
import PatientSearch from './components/PatientSearch';
import PatientProfile from './components/PatientProfile';
import NewVisit from './components/NewVisit';
import Login from './components/Login';
import VisitEditForm from './components/VisitEditForm';
import PatientEdit from './components/PatientEdit';
import ProtectedRoute from './components/ProtectedRoute';

// Import services
import apiService from './services/api';
import authService from './services/authService';

function App() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = authService.isAuthenticated();
      setIsAuthenticated(authStatus);
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Load initial data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated]);

  // Session timeout check
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        if (!authService.isAuthenticated()) {
          handleLogout();
        }
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  /**
   * Loads dashboard data including recent patients
   */
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load recent patients for dashboard
      const recentPatients = await apiService.getRecentPatients();
      setPatients(recentPatients);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles user login
   */
  const handleLogin = (authStatus) => {
    setIsAuthenticated(authStatus);
    if (authStatus) {
      authService.login();
    }
  };

  /**
   * Handles user logout and clears all state
   */
  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setPatients([]);
    setMobileMenuOpen(false);
    setError(null);
  };

  /**
   * Adds a new patient to the system
   */
  const addPatient = async (newPatientData) => {
    try {
      const createdPatient = await apiService.createPatient(newPatientData);
      
      // Add to local state
      setPatients(prev => [createdPatient, ...prev]);
      
      return createdPatient;
    } catch (err) {
      console.error('Failed to create patient:', err);
      throw err;
    }
  };

  /**
   * Adds a new visit for a patient
   */
  const addVisit = async (patientId, visitData) => {
    try {
      const createdVisit = await apiService.createVisit(patientId, visitData);
      
      // Update local patient data
      setPatients(prev => prev.map(patient => 
        patient.id === patientId 
          ? { ...patient, visits: [...(patient.visits || []), createdVisit] }
          : patient
      ));
      
      return createdVisit;
    } catch (err) {
      console.error('Failed to create visit:', err);
      throw err;
    }
  };

  /**
   * Updates patient information
   */
  const updatePatient = async (patientId, updatedData) => {
    try {
      const updatedPatient = await apiService.updatePatient(patientId, updatedData);
      
      // Update local state
      setPatients(prev => prev.map(patient => 
        patient.id === patientId ? updatedPatient : patient
      ));
      
      return updatedPatient;
    } catch (err) {
      console.error('Failed to update patient:', err);
      throw err;
    }
  };

  /**
   * Toggles mobile menu visibility
   */
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  /**
   * Closes mobile menu
   */
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto"></div>
          <p className="mt-4 text-slate-600 font-sans text-sm sm:text-base">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ProtectedRoute isAuthenticated={isAuthenticated} onLogout={handleLogout}>
        <div className="min-h-screen bg-brand-bg">
          {/* Navigation Bar */}
          <nav className="bg-white border-b-2 border-brand-blue sticky top-0 z-50 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-2.5">
                
                {/* Logo & Brand */}
                <Link to="/" className="flex items-center space-x-3 group">
                  <div className="bg-white rounded-lg">
                    <img 
                      src="/assets/logo.png" 
                      alt="Nova Dent Logo" 
                      className="h-14 w-14 object-contain"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-brand-blue font-display font-bold text-xl leading-tight">Nova Dent</span>
                    <span className="text-brand-red font-sans font-semibold text-[9px] tracking-[0.25em] uppercase leading-none">Dental Clinic</span>
                  </div>
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-1">
                  <Link 
                    to="/" 
                    className="px-4 py-2 text-slate-700 font-sans font-semibold text-sm hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/register" 
                    className="px-4 py-2 text-slate-700 font-sans font-semibold text-sm hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
                  >
                    New Patient
                  </Link>
                  <Link 
                    to="/search" 
                    className="px-4 py-2 text-slate-700 font-sans font-semibold text-sm hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
                  >
                    Find Patient
                  </Link>
                  
                  <button 
                    onClick={handleLogout} 
                    className="ml-4 px-4 py-2 bg-slate-100 text-slate-600 rounded-full hover:bg-brand-red hover:text-white transition-all flex items-center text-xs font-bold font-sans"
                  >
                    <LogOut className="h-3.5 w-3.5 mr-2" />
                    LOGOUT
                  </button>
                </div>

                {/* Mobile menu button */}
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6 text-slate-700" />
                  ) : (
                    <Menu className="h-6 w-6 text-slate-700" />
                  )}
                </button>
              </div>

              {/* Mobile Menu */}
              {mobileMenuOpen && (
                <div className="md:hidden py-3 border-t border-slate-200">
                  <Link 
                    to="/" 
                    className="block px-4 py-2 text-slate-700 font-sans font-semibold hover:bg-blue-50 hover:text-brand-blue rounded-lg transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/register" 
                    className="block px-4 py-2 text-slate-700 font-sans font-semibold hover:bg-blue-50 hover:text-brand-blue rounded-lg transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    New Patient
                  </Link>
                  <Link 
                    to="/search" 
                    className="block px-4 py-2 text-slate-700 font-sans font-semibold hover:bg-blue-50 hover:text-brand-blue rounded-lg transition-all"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Find Patient
                  </Link>
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-brand-red font-sans font-semibold hover:bg-red-50 rounded-lg transition-all flex items-center"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Overlay */}
          {mobileMenuOpen && (
            <div 
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={closeMobileMenu}
            />
          )}

          {/* Error Display */}
          {error && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                <p className="text-sm sm:text-base mb-2 font-sans">Error: {error}</p>
                <button 
                  onClick={loadDashboardData}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200 text-sm sm:text-base font-sans font-semibold"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Main Content */}
          <main className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
            <Routes>
              <Route path="/" element={<Dashboard patients={patients} />} />
              <Route path="/register" element={<PatientRegistration onAddPatient={addPatient} />} />
              <Route path="/search" element={<PatientSearch />} />
              <Route path="/patient/:id" element={<PatientProfile onAddVisit={addVisit} onUpdatePatient={updatePatient} />} />
              <Route path="/patient/:id/visit" element={<NewVisit onAddVisit={addVisit} />} />
              <Route path="/patient/:id/edit" element={<PatientEdit />} />
              <Route path="/patient/:id/visit/:visitId/edit" element={<VisitEditForm />} />
            </Routes>
          </main>
        </div>
      </ProtectedRoute>
    </Router>
  );
}

export default App;